<template>
  <AppLoading v-if="!payment" />
  <div v-else-if="payment?.transactions.length === 0">
    <p>No transactions found</p>
  </div>
  <b-table
    v-else
    :items="payment?.transactions"
    :fields="TRACKING_FIELDS"
    thead-class="hidden_header"
    class="payment-items"
  >
    <template #cell(pay_type)="row">
      <div class="payment-cell">
        <strong class="type-text">{{ row.value }}</strong>
        <small class="text-muted">
          On
          <span>{{ toFormattedDate(readDatetimeAsUTC(row.item.timestamp), 'DD MMMM YYYY [at] H:mm A') }} </span>
        </small>
      </div>
    </template>
    <template #cell(creditor_card_pan)="row">
      <div class="payment-cell" v-if="row.item.creditor_card_pan">
        <span>
          Debit card <br />
          <strong>XXXX-XXXX-XXXX-{{ row.item.creditor_card_pan }}</strong>
        </span>
      </div>
      <div class="payment-cell" v-else-if="row.item.creditor_routing_number && row.item.creditor_account_number">
        <span>
          Routing: <strong>{{ row.item.creditor_routing_number }}</strong> <br />
          Account: <strong>{{ row.item.creditor_account_number }}</strong>
        </span>
      </div>
      <div class="payment-cell" v-else-if="row.item.pay_type == 'repayment'"></div>
      <div class="payment-cell" v-else>
        <span> Unable to display payment method </span>
      </div>
    </template>
    <template #cell(status)="row">
      <div class="payment-cell">
        <span v-if="row.item.status === 'COMPLETE'">
          <strong class="h5 text-success">{{ row.value }}</strong>
        </span>
        <span v-else-if="row.item.status === 'FAILED'">
          <strong class="h5 text-danger">{{ row.value }}</strong>
        </span>
        <span v-else>
          <strong class="h5 text-primary">{{ row.value }}</strong>
        </span>
      </div>
    </template>
  </b-table>
</template>

<script setup>
import { useQuery } from '@tanstack/vue-query';
import { getOffCyclePaymentById } from '@/queries/one-time-payments';
import { ref, watch } from 'vue';
import { toCurrency } from '@/utils';
import { toFormattedDate } from '@/utils/date';
import { readDatetimeAsUTC } from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';

const props = defineProps({
  paymentId: {
    type: String,
    required: true,
  },
  visible: {
    type: Boolean,
    default: false,
  },
});

const paymentId = ref(props.paymentId);

const payment = ref(null);
const getPaymentQuery = useQuery({
  ...getOffCyclePaymentById(paymentId),
  enabled: props.visible && !!paymentId.value,
});

watch([getPaymentQuery.fetchStatus, getPaymentQuery.isPending], () => {
  if (getPaymentQuery.data.value) {
    payment.value = getPaymentQuery.data.value;
    payment.value.transactions = payment.value?.transactions.reverse();
  }
});

const TRACKING_FIELDS = [
  {
    key: 'pay_type',
    label: 'Payment type',
    formatter: value => {
      if (value === 'push2Debit') {
        return 'Push to Card';
      }
      if (value === 'instant_payments') {
        return 'RTP/FedNow';
      }
      if (value === 'repayment') {
        return 'Repay Wagestream';
      }
      return value ? value.toUpperCase() : value;
    },
  },
  {
    key: 'sum',
    label: 'Amount',
    formatter: value => {
      return toCurrency(value, 'USD');
    },
  },
  {
    key: 'creditor_card_pan',
    label: 'Account or debit card',
  },
  {
    key: 'status',
    label: 'Payment status',
    formatter: (value, col, row) => {
      if (value === 'SENT') {
        return 'ATTEMPTED';
      }
      if (value === 'FAILED' || row.error_description) {
        return 'FAILED: ' + row.error_description;
      }
      return value;
    },
    thStyle: { width: '30px', textAlign: 'right' },
    tdStyle: { width: '30px' },
    tdClass: 'text-right status-cell',
  },
];
</script>

<style>
.hidden_header {
  display: none;
}

.payment-items {
  width: 90% !important;

  .payment-cell {
    display: flex;
    flex-direction: column;
  }

  .type-text {
    color: var(--palette-color-brand-primary);
    font-size: 15px;
    font-weight: 500;
  }

  .status-cell {
    max-width: 300px;
  }
}
</style>
